import { useProcessorJobStageTasks, useDebouncedSearch } from '@consigli/hooks';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@consigli/shadcn';
import {
  ProcessorJobStage,
  ProcessorJobStageTask,
  ProcessorJobStageTaskStatus,
} from '@consigli/types';
import { formatEnumToReadableText } from '@consigli/utils';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState, useCallback } from 'react';

import {
  Badge,
  DataTable,
  ObjectDetailView,
  Pagination,
  Search,
  Select,
  DEFAULT_PAGE_SIZE,
} from '@/components';
import { taskStatusMapping } from '@/utils/task-status-mapping';
import { formatTimestamp } from '@/utils/timestamp-converting';

interface TasksSheetProps {
  projectId: number;
  jobId: string;
  stage: ProcessorJobStage;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_ALL = 'ALL' as const;

type StatusFilterOption = {
  label: string;
  value: ProcessorJobStageTaskStatus | typeof STATUS_ALL;
};

const statusFilterOptions: StatusFilterOption[] = [
  { label: 'All', value: STATUS_ALL },
  { label: 'Initialized', value: ProcessorJobStageTaskStatus.INITIALIZED },
  { label: 'Started', value: ProcessorJobStageTaskStatus.STARTED },
  { label: 'Completed', value: ProcessorJobStageTaskStatus.COMPLETED },
  { label: 'Failed', value: ProcessorJobStageTaskStatus.FAILED },
  { label: 'Skipped', value: ProcessorJobStageTaskStatus.SKIPPED },
];

export const TasksSheet = ({ projectId, jobId, stage, isOpen, onOpenChange }: TasksSheetProps) => {
  const pageSize = DEFAULT_PAGE_SIZE;
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedSearch(searchTerm, 500);
  const [selectedStatus, setSelectedStatus] = useState<StatusFilterOption>(statusFilterOptions[0]);
  const [selectedTask, setSelectedTask] = useState<ProcessorJobStageTask | null>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);

  const { tasks, paginatedCount, isFetching } = useProcessorJobStageTasks({
    projectId,
    jobId,
    stageId: stage.stageName,
    page,
    pageSize,
    search: debouncedSearchTerm || undefined,
    status: selectedStatus.value === STATUS_ALL ? undefined : selectedStatus.value,
  });

  const handleViewDetails = useCallback((task: ProcessorJobStageTask) => {
    setSelectedTask(task);
    setIsDetailViewOpen(true);
  }, []);

  const handleCloseDetailView = useCallback((open: boolean) => {
    setIsDetailViewOpen(open);
    if (!open) {
      setSelectedTask(null);
    }
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    setSearchTerm(e.target.value);
  }, []);

  const handleStatusChange = useCallback((option: StatusFilterOption) => {
    setSelectedStatus(option);
    setPage(1);
  }, []);

  const columns: ColumnDef<ProcessorJobStageTask>[] = useMemo(
    () => [
      {
        header: 'Task Name',
        accessorKey: 'taskName',
        size: 150,
        cell: ({ getValue }) => {
          const value = getValue() as string;
          return (
            <span className="max-w-[150px] truncate block" title={value}>
              {value}
            </span>
          );
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ getValue }) => {
          const status = getValue() as ProcessorJobStageTaskStatus;
          const config = taskStatusMapping[status];
          return <Badge text={config.text} variant={config.variant} />;
        },
      },
      {
        header: 'Processor',
        accessorKey: 'processorName',
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return <span>{value ?? '—'}</span>;
        },
      },
      {
        header: 'Status Description',
        accessorKey: 'statusDescription',
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return (
            <span className="max-w-xs truncate block" title={value ?? ''}>
              {value ?? '—'}
            </span>
          );
        },
      },
      {
        header: 'Created At',
        accessorKey: 'createdAt',
        cell: ({ getValue }) => {
          const createdAt = getValue() as string;
          return <span className="text-xs">{formatTimestamp(createdAt)}</span>;
        },
      },
      {
        header: 'Updated At',
        accessorKey: 'updatedAt',
        cell: ({ getValue }) => {
          const updatedAt = getValue() as string;
          return <span className="text-xs">{formatTimestamp(updatedAt)}</span>;
        },
      },
    ],
    [],
  );

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="sm:max-w-2xl !gap-0 flex flex-col overflow-hidden">
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <SheetTitle className="text-lg font-semibold">
              {formatEnumToReadableText(stage.stageName)} — Tasks
            </SheetTitle>
            <SheetDescription className="text-sm text-gray-500">
              {stage.completedTasks}/{stage.totalTasks} completed
              {stage.totalFailedTasks > 0 && (
                <span className="text-red-600 ml-2">({stage.totalFailedTasks} failed)</span>
              )}
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-wrap gap-3 px-6 pt-4 pb-2 shrink-0">
            <Search
              text={searchTerm}
              placeholder="Search by processor name"
              onChange={handleSearch}
              className="w-[250px]"
            />
            <Select
              options={statusFilterOptions}
              value={selectedStatus}
              onValueChange={handleStatusChange}
              placeholder="Filter by status"
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.value}
              className="w-[250px] h-[40px] text-sm bg-white shadow-none"
            />
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <DataTable
              columns={columns}
              data={tasks}
              isLoading={isFetching}
              onRowClick={handleViewDetails}
            />
            {paginatedCount > pageSize && (
              <div className="mt-4">
                <Pagination
                  onPageChange={handlePageChange}
                  pageSize={pageSize}
                  currentPage={page}
                  totalCount={paginatedCount}
                />
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {selectedTask && (
        <ObjectDetailView
          object={selectedTask}
          objectType="Task"
          summary={`Task "${selectedTask.taskName}" details including status and processor information.`}
          isOpen={isDetailViewOpen}
          onOpenChange={handleCloseDetailView}
        />
      )}
    </>
  );
};
